import React, { useState, useEffect, useRef } from 'react';
import api from '../../../../../api';
import constants from '../../../../../util_funs/constant';
import './VoiceModals.css';

const VoiceIntegrityModal = ({ userId, voiceIntegrityData, isEditMode, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useRef(null);
  const modalContentRef = useRef(null);
  const [formData, setFormData] = useState({
    friendlyName: '',
    endUserName: '',
    useCase: '',
    employeeCount: '',
    avgCallVolume: '',
    notes: '',
  });
  const [errors, setErrors] = useState({
    friendlyName: '',
    endUserName: '',
    useCase: '',
    employeeCount: '',
    avgCallVolume: '',
    notes: '',
  });
  const [originalFormData, setOriginalFormData] = useState(null);

  const isRejected = voiceIntegrityData?.status === 'twilio-rejected';

  useEffect(() => {
    if (isEditMode && voiceIntegrityData) {
      const data = {
        friendlyName: voiceIntegrityData.friendly_name || '',
        endUserName: voiceIntegrityData.end_user_friendly_name || '',
        useCase: voiceIntegrityData.use_case || '',
        employeeCount: voiceIntegrityData.employee_count ? String(voiceIntegrityData.employee_count) : '',
        avgCallVolume: voiceIntegrityData.avg_call_volume ? String(voiceIntegrityData.avg_call_volume) : '',
        notes: voiceIntegrityData.notes || '',
      };
      setFormData(data);
      setOriginalFormData(data);
    }
  }, [isEditMode, voiceIntegrityData]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Scroll to top when error appears
  useEffect(() => {
    if (error && modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [error]);

  // Check if form has changed
  const hasFormChanged = () => {
    if (!originalFormData) return true;
    return (
      formData.friendlyName !== originalFormData.friendlyName ||
      formData.endUserName !== originalFormData.endUserName ||
      formData.useCase !== originalFormData.useCase ||
      formData.employeeCount !== originalFormData.employeeCount ||
      formData.avgCallVolume !== originalFormData.avgCallVolume ||
      formData.notes !== originalFormData.notes
    );
  };

  const validate = () => {
    const e = {
      friendlyName: '',
      endUserName: '',
      useCase: '',
      employeeCount: '',
      avgCallVolume: '',
      notes: '',
    };
    let ok = true;

    // Friendly Name
    if (!formData.friendlyName.trim()) {
      e.friendlyName = 'Voice Integrity Friendly Name is required';
      ok = false;
    } else if (formData.friendlyName.length > 64) {
      e.friendlyName = 'Must be 64 characters or less';
      ok = false;
    }

    // End User Name
    if (!formData.endUserName.trim()) {
      e.endUserName = 'End User Friendly Name is required';
      ok = false;
    } else if (formData.endUserName.length < 2) {
      e.endUserName = 'Must be at least 2 characters';
      ok = false;
    }

    // Use Case
    if (!formData.useCase) {
      e.useCase = 'Use Case is required';
      ok = false;
    } else if (!constants.VOICE_INTEGRITY_USE_CASE_OPTIONS.find(opt => opt.value === formData.useCase)) {
      e.useCase = 'Invalid use case selected';
      ok = false;
    }

    // Employee Count
    if (!formData.employeeCount.trim()) {
      e.employeeCount = 'Employee count is required';
      ok = false;
    } else {
      const num = Number(formData.employeeCount);
      if (isNaN(num) || num <= 0) {
        e.employeeCount = 'Employee count must be greater than 0';
        ok = false;
      }
    }

    // Avg Call Volume
    if (!formData.avgCallVolume.trim()) {
      e.avgCallVolume = 'Average call volume is required';
      ok = false;
    } else {
      const num = Number(formData.avgCallVolume);
      if (isNaN(num) || num <= 0) {
        e.avgCallVolume = 'Average call volume must be greater than 0';
        ok = false;
      }
    }

    // Notes (optional)
    if (formData.notes && formData.notes.length > 500) {
      e.notes = 'Notes cannot exceed 500 characters';
      ok = false;
    }

    setErrors(e);
    return ok;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      setError('Please fill in all required fields before proceeding.');
      // Scroll to top to show validation errors
      setTimeout(() => {
        if (modalContentRef.current) {
          modalContentRef.current.scrollTop = 0;
        }
      }, 100);
      return;
    }

    // Check if form has changed in edit mode
    if (isEditMode && !hasFormChanged()) {
      setError('No changes detected. Please modify at least one field to update.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        friendly_name: formData.friendlyName,
        end_user_friendly_name: formData.endUserName,
        use_case: formData.useCase,
        employee_count: Number(formData.employeeCount),
        avg_call_volume: Number(formData.avgCallVolume),
        notes: formData.notes,
        user_id: parseInt(userId),
      };

      // Use createVoiceIntegrity for both create and update
      const response = await api.outbound.createVoiceIntegrity(payload);

      if (response?.status) {
        onClose(true); // Close modal and trigger refresh
      } else {
        // Handle validation errors from API
        if (response?.errors && typeof response.errors === 'object') {
          const errorMessages = Object.entries(response.errors)
            .map(([field, messages]) => {
              const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              const msgList = Array.isArray(messages) ? messages : [messages];
              return `${fieldName}: ${msgList.join(', ')}`;
            })
            .join('\n');
          setError(errorMessages || response?.message || 'Validation failed. Please check your inputs.');
        } else {
          setError(response?.message || (isEditMode ? 'Failed to update Voice Integrity' : 'Failed to create Voice Integrity'));
        }
        // Scroll to top to show error message
        setTimeout(() => {
          if (modalContentRef.current) {
            modalContentRef.current.scrollTop = 0;
          }
        }, 100);
      }
    } catch (err) {
      console.error('Voice Integrity Submit Error:', err);
      setError(err?.message || 'Something went wrong');
      // Scroll to top to show error message
      setTimeout(() => {
        if (modalContentRef.current) {
          modalContentRef.current.scrollTop = 0;
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
    // Clear form-level error
    if (error) {
      setError('');
    }
  };

  const getModalTitle = () => {
    if (isRejected) return 'Resubmit Voice Integrity';
    if (isEditMode) return 'Edit Voice Integrity';
    return 'Create Voice Integrity';
  };

  const getSubmitButtonText = () => {
    if (loading) {
      return isRejected ? 'Resubmitting...' : (isEditMode ? 'Updating...' : 'Creating...');
    }
    return isRejected ? 'Resubmit' : (isEditMode ? 'Update Voice Integrity' : 'Create Voice Integrity');
  };

  return (
    <div className="voice-modal-overlay">
      <div className="voice-modal-content">
        <div className="voice-modal-header">
          <h3>{getModalTitle()}</h3>
          <button
            className="voice-modal-close"
            onClick={() => onClose(false)}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
          <div className="voice-modal-body" ref={modalContentRef} style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
            {/* Rejection Reason Alert */}
            {isRejected && voiceIntegrityData?.rejection_reason && (
              <div className="warning-box">
                <p>
                  <strong>Previous Rejection Reason:</strong> {voiceIntegrityData.rejection_reason}
                </p>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div className="alert alert-danger" ref={errorRef} style={{ whiteSpace: 'pre-line' }}>
                {error}
              </div>
            )}

            {/* Info Box */}
            <div className="info-box">
             
              <p>
                Voice Integrity registers your verified business with carrier analytics engines
                to prevent legitimate calls from being flagged as spam, improving answer rates.
              </p>
            </div>

            {/* Friendly Name */}
            <div className="form-group">
              <label>Voice Integrity Friendly Name <span className="required">*</span></label>
              <input
                type="text"
                name="friendlyName"
                className={`form-input ${errors.friendlyName ? 'error' : ''}`}
                value={formData.friendlyName}
                onChange={handleInputChange}
                placeholder="e.g., ACME Voice Integrity"
                disabled={loading}
              />
              {errors.friendlyName && (
                <span className="error-message">{errors.friendlyName}</span>
              )}
              <span className="field-hint">A friendly name to identify this Voice Integrity trust product</span>
            </div>

            {/* End User Name */}
            <div className="form-group">
              <label>End User Friendly Name <span className="required">*</span></label>
              <input
                type="text"
                name="endUserName"
                className={`form-input ${errors.endUserName ? 'error' : ''}`}
                value={formData.endUserName}
                onChange={handleInputChange}
                placeholder="e.g., ACME Corporation"
                disabled={loading}
              />
              {errors.endUserName && (
                <span className="error-message">{errors.endUserName}</span>
              )}
              <span className="field-hint">The legal or commonly known name of the end user or business</span>
            </div>

            {/* Use Case */}
            <div className="form-group">
              <label>Use Case <span className="required">*</span></label>
              <select
                name="useCase"
                className={`form-input ${errors.useCase ? 'error' : ''}`}
                value={formData.useCase}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value="">Select Use Case</option>
                {constants.VOICE_INTEGRITY_USE_CASE_OPTIONS.map(useCase => (
                  <option key={useCase.value} value={useCase.value}>
                    {useCase.text}
                  </option>
                ))}
              </select>
              {errors.useCase && (
                <span className="error-message">{errors.useCase}</span>
              )}
              <span className="field-hint">Select the primary use case for your voice calls</span>
            </div>

            {/* Employee Count */}
            <div className="form-group">
              <label>Business Employee Count <span className="required">*</span></label>
              <input
                type="number"
                name="employeeCount"
                min="1"
                className={`form-input ${errors.employeeCount ? 'error' : ''}`}
                value={formData.employeeCount}
                onChange={handleInputChange}
                placeholder="e.g., 25"
                disabled={loading}
              />
              {errors.employeeCount && (
                <span className="error-message">{errors.employeeCount}</span>
              )}
              <span className="field-hint">Total number of employees in your business</span>
            </div>

            {/* Avg Call Volume */}
            <div className="form-group">
              <label>Average Business Day Call Volume <span className="required">*</span></label>
              <input
                type="number"
                name="avgCallVolume"
                min="1"
                className={`form-input ${errors.avgCallVolume ? 'error' : ''}`}
                value={formData.avgCallVolume}
                onChange={handleInputChange}
                placeholder="e.g., 120"
                disabled={loading}
              />
              {errors.avgCallVolume && (
                <span className="error-message">{errors.avgCallVolume}</span>
              )}
              <span className="field-hint">Average number of calls per business day</span>
            </div>

            {/* Notes */}
            <div className="form-group">
              <label>Notes (optional)</label>
              <textarea
                name="notes"
                className="form-input"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder='Add any additional details about your use case.
Example: "We do last-mile delivery of goods and notify customers before arrival."'
                rows="4"
                disabled={loading}
                maxLength={500}
                style={{ resize: 'vertical', minHeight: '100px' }}
              />
              {errors.notes && (
                <span className="error-message">{errors.notes}</span>
              )}
              <span className="field-hint">Additional details about your voice calling use case (max 500 characters)</span>
              <div className="character-count">
                {formData.notes.length}/500 characters
              </div>
            </div>

            {/* Warning Box */}
           
          </div>

          <div className="voice-modal-footer">
            <button
              type="button"
              className="btn linear-gradient-cancel yr-cancelbtn mg-r"
              onClick={() => onClose(false)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn linear-gradient zy-save-btn ml-2"
              disabled={loading || (isEditMode && !hasFormChanged())}
            >
              {loading && (
                <span className="spinner-border spinner-border-sm" style={{ marginRight: '8px' }} role="status" aria-hidden="true"></span>
              )}
              {getSubmitButtonText()}
            </button>
          </div>
        </form>

        {/* Loading Overlay */}
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div className="ui active inline loader" style={{ marginBottom: '15px' }}></div>
              <p style={{ color: '#087ab6', fontSize: '14px', fontWeight: 500 }}>
                {isRejected ? 'Resubmitting Voice Integrity...' : (isEditMode ? 'Updating Voice Integrity...' : 'Creating Voice Integrity...')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceIntegrityModal;
